import { ReactNode } from 'react';

import moment from 'moment';

import { DataGridColSettings } from '@/components/DataGrid/utils';
import {
  EthnicOriginList,
  citizenOfInput,
  eyeColorInput,
  genderInput,
  hairColorInput,
  LanguageList,
  MaritalStatus,
  NationalityList,
  RaceList,
} from '@/constants/form_inputs';
import { OtherPhysicalCharacteristics } from '@/models';
import {
  InmateAgeCategory,
  InmateStatus,
} from '@/pages/JMS/Managament/types/inmate';

const raceInput = {
  name: 'race',
  type: 'autocomplete',
  label: 'Race',
  items: [
    ...Object.entries(RaceList).map(([value, label]) => ({
      label,
      value,
    })),
  ],
};

const nationalityInput = {
  name: 'nationality',
  type: 'autocomplete',
  label: 'Nationality',
  items: [
    ...Object.entries(NationalityList).map(([value, label]) => ({
      label,
      value,
    })),
  ],
};

const ethnicRaceInput = {
  name: 'ethnicOrigin',
  type: 'autocomplete',
  label: 'Ethnic origin',
  items: [
    ...Object.entries(EthnicOriginList).map(([value, label]) => ({
      label,
      value,
    })),
  ],
};

export interface IJMSFormFieldProps extends React.ComponentProps<'input'> {
  name: string;
  type: string;
  label: string;
  className: string;
  placeholder?: string;
  min?: string;
  max?: string;
  objectField?: string;
  format?: string;
  multiple?: boolean;
  items?:
    | {
        label: string;
        value: string | { _id: string };
      }[]
    | string;
  additionalLabel?: string;
  tab?: string;
  disabledDelete?: boolean;
  disabled?: boolean;
  hideClear?: boolean;
  itemAction?: ReactNode;
  hideSpinner?: boolean;
  isModal?: boolean;
}

export type IJMSFormSections = {
  title: string;
  items: IJMSFormFieldProps[];
  name?: string;
  multiple?: boolean;
  label?: string;
}[];

export const InmateForm: IJMSFormSections = [
  {
    title: 'Identity',
    name: 'identity',
    items: [
      {
        name: 'identity.suffix',
        type: 'text',
        label: 'Suffix',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'identity.lastName',
        type: 'text',
        label: 'Last name',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'identity.firstName',
        type: 'text',
        label: 'First name',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'identity.middleName',
        type: 'text',
        label: 'Middle name',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'identity.aliasName',
        type: 'text',
        label: 'Alias name',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'identity.nickName',
        type: 'text',
        label: 'Nickname',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'identity.birthday',
        type: 'date',
        label: 'Date of birth',
        min: moment('1900-01-01').toISOString(),
        max: moment().toISOString(),
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'identity.ageCategory',
        type: 'select',
        label: 'Age category',
        className: 'col-span-2',
        placeholder: '',
        items: [
          {
            label: 'Adult',
            value: InmateAgeCategory.ADULT,
          },
          {
            label: 'Juvenile',
            value: InmateAgeCategory.JUVENILE,
          },
        ],
      },
      {
        name: 'inmate.iid',
        type: 'text',
        label: 'Inmate ID/Number',
        className: 'col-span-2',
        placeholder: '',
        disabled: true,
      },
      {
        ...genderInput,
        name: 'identity.sex',
        type: 'select',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        ...raceInput,
        name: 'identity.race',
        type: 'select',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        ...ethnicRaceInput,
        name: 'identity.ethnicOrigin',
        type: 'select',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'identity.physicalCharacteristics.heightExact',
        type: 'heightExactInput',
        hideSpinner: true,
        label: 'Height (ft/in)',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'identity.physicalCharacteristics.weight',
        type: 'numberInput',
        hideSpinner: true,
        label: 'Weight (lb)',
        className: 'col-span-1',
        placeholder: '',
      },
      {
        ...hairColorInput,
        name: 'identity.physicalCharacteristics.hairColor',
        type: 'select',
        className: 'col-span-1',
        placeholder: '',
      },
      {
        ...eyeColorInput,
        name: 'identity.physicalCharacteristics.eyeColor',
        type: 'select',
        className: 'col-span-1',
        placeholder: '',
      },
      {
        name: 'identity.physicalCharacteristics.bodyMarking',
        type: 'text',
        label: 'Scars, marks, and tattoos',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'identity.physicalCharacteristics.others',
        type: 'select',
        multiple: true,
        label: 'Other physical characteristics',
        className: 'col-span-2',
        placeholder: '',
        items: [
          { label: 'Mustache', value: OtherPhysicalCharacteristics.MUSTACHE },
          { label: 'Beared', value: OtherPhysicalCharacteristics.BEARED },
          { label: 'Big Eyes', value: OtherPhysicalCharacteristics.BIG_EYES },
          {
            label: 'Short Hair',
            value: OtherPhysicalCharacteristics.SHORT_HAIR,
          },
          { label: 'Long Hair', value: OtherPhysicalCharacteristics.LONG_HAIR },
          {
            label: 'Straight Hair',
            value: OtherPhysicalCharacteristics.STRAIGHT_HAIR,
          },
          { label: 'Curls', value: OtherPhysicalCharacteristics.CURLS },
          { label: 'Freckles', value: OtherPhysicalCharacteristics.FRECKLES },
        ],
      },
    ],
  },
  {
    title: 'Personal Information',
    name: 'profile',
    items: [
      {
        name: 'identity.ssn',
        type: 'ssn',
        label: 'Social security number',
        className: 'col-span-2',
        placeholder: '___-__-____',
        format: `${'\u2217'}${'\u2217'}${'\u2217'}${'\u2217'}${'\u2217'}9999`,
      },
      {
        name: 'identity.nysid',
        type: 'text',
        label: 'NYSID',
        className: 'col-span-2',
        placeholder: '',
        maxLength: 9,
      },
      {
        name: 'identity.fbiNumber',
        type: 'text',
        label: 'FBI Number',
        className: 'col-span-2',
        placeholder: '',
        maxLength: 9,
      },
      {
        ...nationalityInput,
        name: 'identity.nationality',
        type: 'select',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'identity.maritalStatus',
        type: 'select',
        label: 'Marital status',
        className: 'col-span-2',
        placeholder: '',
        items: Object.entries(MaritalStatus).map(([code, val]) => ({
          label: `${code} - ${val}`,
          value: val,
        })),
      },
      {
        name: 'identity.spokenLanguages',
        type: 'select',
        label: 'Languages spoken',
        className: 'col-span-2',
        placeholder: '',
        multiple: true,
        items: Object.keys(LanguageList).map((key) => ({
          label: LanguageList[key as keyof typeof LanguageList],
          value: key as keyof typeof LanguageList,
        })),
      },
      {
        name: 'identity.inmateStatus',
        type: 'select',
        label: 'Status',
        className: 'col-span-2',
        placeholder: '',
        disabled: true,
        items: [
          {
            label: 'Processing',
            value: InmateStatus.PROCESSING,
          },
          {
            label: 'Processed',
            value: InmateStatus.PROCESSED,
          },
          {
            label: 'Released',
            value: InmateStatus.RELEASED,
          },
        ],
      },
      {
        name: 'identity.usMarshallNumber',
        type: 'text',
        label: 'US Marshall Number',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        ...citizenOfInput,
        name: 'identity.citizenOf',
        type: 'select',
        defaultValue: '',
        className: 'col-span-2',
      },
    ],
  },
  {
    title: 'Contact Information',
    name: 'contactInfo',
    items: [
      {
        label: 'Transient (Address not required)',
        name: 'identity.transient',
        type: 'checkbox',
        className: 'col-span-6 lg:col-span-2 mt-7',
        placeholder: '',
      },
      {
        name: 'identity.addresses',
        type: 'address',
        label: '',
        additionalLabel: 'address',
        className: 'col-span-6',
        placeholder: '',
        multiple: true,
      },
      {
        name: 'identity.phoneNumbers',
        type: 'phoneNumber',
        label: 'Phone number',
        additionalLabel: 'phone number',
        className: 'col-span-4',
        placeholder: '',
        multiple: true,
      },
    ],
  },
  {
    title: 'Employment Information',
    name: 'employerInfo',
    items: [
      {
        name: 'identity.employerInfo.name',
        type: 'text',
        label: 'Employer name',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'identity.employerInfo.occupation',
        type: 'text',
        label: 'Occupation',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'identity.employerInfo.address',
        type: 'address',
        label: 'Employer Address',
        className: 'col-span-4',
        placeholder: '',
      },
      {
        name: 'identity.employerInfo.phoneNumber',
        type: 'phoneNumber',
        label: 'Employer Phone',
        className: 'col-span-2',
        placeholder: '(___) ___-____',
        format: '(999) 999-9999',
      },
    ],
  },
];

export const DischargeTypeList = [
  {
    label: 'Adult, Released from Custody',
    value: 'Adult, Released from Custody',
  },
  {
    label: 'Adult, Released on OR Bond',
    value: 'Adult, Released on OR Bond',
  },
  {
    label: 'Adult, Released to Other Adult',
    value: 'Adult, Released to Other Adult',
  },
  {
    label: 'Adult, Released to Other Fcty',
    value: 'Adult, Released to Other Fcty',
  },
  {
    label: 'Adult, Temp Release',
    value: 'Adult, Temp Release',
  },
  {
    label: 'Juv, Handled and Released',
    value: 'Juv, Handled and Released',
  },
  {
    label: 'Juv, Refer to Juv Authy',
    value: 'Juv, Refer to Juv Authy',
  },
  {
    label: 'Juv, Released to Adult Authy',
    value: 'Juv, Released to Adult Authy',
  },
  {
    label: 'Juv, Released to Other Agncy',
    value: 'Juv, Released to Other Agncy',
  },
  {
    label: 'Juv, Released to Welfare Agncy',
    value: 'Juv, Released to Welfare Agncy',
  },
];

export const BookingDispositionList = [
  {
    label: 'TBD',
    value: 'TBD',
  },
];

export const BookingAgencyList = [
  {
    label: "Platte County Sheriff's Office",
    value: "Platte County Sheriff's Office",
  },
  {
    label: 'Wheatland Police Department',
    value: 'Wheatland Police Department',
  },
  {
    label: 'Guernsey Police Department',
    value: 'Guernsey Police Department',
  },
  {
    label: 'Wyoming Highway Patrol',
    value: 'Wyoming Highway Patrol',
  },
  {
    label: 'Wyoming State Parks',
    value: 'Wyoming State Parks',
  },
  {
    label: 'Wyoming Game & Fish',
    value: 'Wyoming Game & Fish',
  },
  {
    label: 'Wyoming Division of Criminal Investigation',
    value: 'Wyoming Division of Criminal Investigation',
  },
  {
    label: 'Wyoming Department of Corrections',
    value: 'Wyoming Department of Corrections',
  },
  {
    label: 'Wyoming Probation and Parole',
    value: 'Wyoming Probation and Parole',
  },
  {
    label: 'Cheyenne Police Department',
    value: 'Cheyenne Police Department',
  },
  {
    label: 'United States Marshal Service',
    value: 'United States Marshal Service',
  },
  {
    label: 'Immigration and Customs Enforcment',
    value: 'Immigration and Customs Enforcment',
  },
  {
    label: 'Courtesy Hold',
    value: 'Courtesy Hold',
  },
  {
    label: 'DEA',
    value: 'DEA',
  },
  {
    label: 'ATF',
    value: 'ATF',
  },
  {
    label: 'FBI',
    value: 'FBI',
  },
];

export const InmateBookingTypeList = [
  {
    label: 'New Arrest',
    value: 'New Arrest',
  },
  {
    label: 'Warrant Arrest',
    value: 'Warrant Arrest',
  },
  {
    label: 'Hold for other Agency',
    value: 'Hold for other Agency',
  },
  {
    label: 'Court Incarcerated',
    value: 'Court Incarcerated',
  },
];

export const BookingForm: IJMSFormSections = [
  {
    title: 'General Booking Info',
    name: 'bookingInfo',
    items: [
      {
        name: 'details.number',
        type: 'text',
        label: 'Booking number',
        className: 'col-span-2',
        placeholder: '',
        disabled: true,
      },
      {
        name: 'details.agency',
        type: 'select',
        label: 'Agency booked for',
        className: 'col-span-2',
        placeholder: '',
        items: 'getAgencies',
      },
      {
        name: 'details.type',
        type: 'select',
        label: 'Booking Type',
        className: 'col-span-2',
        placeholder: '',
        items: InmateBookingTypeList,
      },
      {
        name: 'details.arrivalCredit',
        type: 'date',
        label: 'Arrival credit',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'details.initialBooking',
        type: 'date',
        label: 'Initial Booking Date',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'details.lastIntake',
        type: 'date',
        label: 'Last Intake Date',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'details.kase',
        type: 'asyncSelect',
        label: 'Case Number',
        className: 'col-span-2',
        placeholder: '',
        items: 'getKases',
        itemAction: 'kaseClickout',
      },
      {
        name: 'details.bookingDisposition',
        type: 'select',
        label: 'Booking Disposition',
        className: 'col-span-2',
        placeholder: '',
        items: BookingDispositionList,
      },
      {
        name: 'details.dischargeType',
        type: 'select',
        label: 'Discharge Type',
        className: 'col-span-2',
        placeholder: '',
        items: DischargeTypeList,
      },
      {
        name: 'details.scheduledReleaseDate',
        type: 'date',
        label: 'Schedule Release',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'details.actualReleaseDate',
        type: 'date',
        label: 'Actual Release',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'details.bondPaymentDue',
        type: 'text',
        label: 'Req bond payment due',
        className: 'col-span-2',
        placeholder: '',
      },
      {
        name: 'details.bookingOfficer',
        type: 'select',
        label: 'Booking Officer',
        className: 'col-span-2',
        placeholder: '',
        items: 'getUsers',
      },
      {
        name: 'details.chargeHousing',
        type: 'checkbox',
        label: 'Charge housing',
        className: 'justify-end col-span-2',
        placeholder: '',
        checked: true,
      },
    ],
  },
];

export const inmateDataGridColumns: DataGridColSettings[] = [
  {
    field: 'fullName',
    headerName: 'Full name',
    visible: true,
  },
  {
    field: 'inmateId',
    headerName: 'Inmate ID',
    width: 160,
    visible: true,
  },
  {
    field: 'gender',
    headerName: 'Gender',
    width: 160,
    visible: true,
  },
  {
    field: 'dateOfBirth',
    headerName: 'Date of Birth',
    width: 160,
    visible: true,
  },
  {
    field: 'housing',
    headerName: 'Housing',
    width: 160,
    visible: true,
  },
  {
    field: 'location',
    headerName: 'Current Location',
    width: 160,
    visible: true,
  },
  {
    field: 'ssn',
    headerName: 'Social Security Number',
    width: 160,
    visible: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 160,
    visible: true,
  },
  {
    field: 'flags',
    headerName: 'Flags',
    width: 160,
    visible: true,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 140,
    visible: true,
  },
];

export const bookingDataGridColumns: DataGridColSettings[] = [
  {
    field: 'bookingNumber',
    headerName: 'Booking number',
    visible: true,
  },
  {
    field: 'inmate',
    headerName: 'Inmate',
    visible: true,
  },
  {
    field: 'agency',
    headerName: 'Agency booked for',
    visible: true,
  },
  {
    field: 'bookingType',
    headerName: 'Booking type',
    visible: true,
  },
  {
    field: 'bookingOfficer',
    headerName: 'Booking officer',
    visible: true,
  },
  {
    field: 'initialBookingDate',
    headerName: 'Initial booking date',
    visible: true,
  },
  {
    field: 'caseNumber',
    headerName: 'Case number',
    visible: true,
  },
  {
    field: 'scheduledRelease',
    headerName: 'Scheduled release',
    visible: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    visible: true,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    visible: true,
  },
];

export const InmateNotesGridColumns: DataGridColSettings[] = [
  {
    field: 'description',
    headerName: 'Note Description',
    visible: true,
  },
  {
    field: 'officer',
    headerName: 'Officer',
    visible: true,
  },
  {
    field: 'createdBy',
    headerName: 'Created by',
    visible: true,
  },
  {
    field: 'createdAt',
    headerName: 'Created date',
    visible: true,
  },
];
